/**
 * BufferManagerDrawer - Right-side slide-out drawer for full buffer CRUD
 *
 * Provides searchable/filterable buffer list with create, rename, delete,
 * and a detail panel with versions, timeline, and semantic similarity.
 *
 * Phase 1: List view with search, filter, CRUD
 * Phase 2: Detail panel with versions/timeline/similar tabs
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useBufferSync, cleanBufferName, type BufferSummary } from '../../contexts/BufferSyncContext';
import { usePlatform } from '../../contexts/PlatformContext';
import type { BufferType } from '../../contexts/ApiContext';
import type { BufferContentData } from '../../types/electron';
import { BufferTimeline } from './BufferTimeline';
import { createLogger } from '../../utils/debug';

const log = createLogger('BufferManager');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface BufferManagerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

type TypeFilter = 'all' | BufferType;
type SortMode = 'date-desc' | 'date-asc' | 'name-asc' | 'name-desc';
type DetailTab = 'versions' | 'timeline' | 'similar';

interface VersionEntry {
  id: string;
  bufferId: string;
  text: string;
  wordCount: number;
  label: string;
  description: string | null;
  trigger: string;
  createdAt: number;
}

interface SimilarBuffer {
  id: string;
  name: string;
  wordCount: number;
  similarity: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const TYPE_LABELS: Record<BufferType, string> = {
  working: 'W',
  voice: 'V',
  reference: 'R',
  arc: 'A',
};

const TYPE_NAMES: Record<BufferType, string> = {
  working: 'Working',
  voice: 'Voice',
  reference: 'Reference',
  arc: 'Arc',
};

function relativeTime(ts: number | string): string {
  const time = typeof ts === 'string' ? new Date(ts).getTime() : ts;
  const diff = Date.now() - time;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(time).toLocaleDateString();
}

function formatWords(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

/** Extract a meaningful preview from buffer text (first ~60 chars of real content) */
function contentPreview(text: string | undefined, maxLen = 60): string {
  if (!text) return '';
  // Strip HTML comments (thread serialization markers)
  const clean = text.replace(/<!--[^>]*-->\n?/g, '').trim();
  // Take first line, strip markdown headers
  let firstLine = clean.split('\n')[0]?.replace(/^#+\s*/, '').trim() ?? '';
  // Handle markdown image links: ![alt](url) → "[Image: alt]" or "[Image]"
  firstLine = firstLine.replace(/!\[([^\]]*)\]\([^)]+\)/g, (_m, alt) =>
    alt.trim() ? `[Image: ${alt.trim()}]` : '[Image]'
  );
  // Handle markdown links: [text](url) → text
  firstLine = firstLine.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Handle standalone URLs → domain
  firstLine = firstLine.replace(/https?:\/\/\S+/g, (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return url; }
  });
  if (firstLine.length <= maxLen) return firstLine;
  return firstLine.slice(0, maxLen - 1).trimEnd() + '\u2026';
}

/** Build a source label from origin metadata */
function sourceLabel(rich: BufferContentData | undefined): string {
  if (!rich) return '';
  const parts: string[] = [];
  const platform = rich.origin.sourcePlatform;
  if (platform) parts.push(platform);
  const sourceType = rich.origin.sourceType;
  if (sourceType && sourceType !== 'manual') parts.push(sourceType);
  const author = rich.origin.author;
  if (author) parts.push(author);
  return parts.join(' \u00b7 ');
}

/** Check if a buffer name is generic/meaningless */
function isGenericName(name: string): boolean {
  const lower = name.toLowerCase().trim();
  return lower === 'thread'
    || lower === 'workspace'
    || /^[0-9a-f-]{8,}$/.test(lower)
    || /^!\[/.test(lower)           // markdown image link
    || /^https?:\/\//.test(lower)   // raw URL
    || lower === 'image';
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function BufferManagerDrawer({ isOpen, onClose }: BufferManagerDrawerProps) {
  const {
    activeBufferName,
    buffers,
    switchBuffer,
    createBuffer,
    deleteBuffer,
    refreshBuffers,
  } = useBufferSync();
  const { isElectron } = usePlatform();

  // List state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [sortMode, setSortMode] = useState<SortMode>('date-desc');
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<BufferType>('working');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  // Detail state (Phase 2)
  const [detailBuffer, setDetailBuffer] = useState<BufferContentData | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>('versions');
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [similarBuffers, setSimilarBuffers] = useState<SimilarBuffer[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [checkpointLabel, setCheckpointLabel] = useState('');

  // Rich buffer data (Electron only — has metadata beyond BufferSummary)
  const [richBuffers, setRichBuffers] = useState<BufferContentData[]>([]);

  const searchRef = useRef<HTMLInputElement>(null);
  const createRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Refresh buffers when drawer opens
  useEffect(() => {
    if (isOpen) {
      refreshBuffers();
      if (isElectron && window.electronBuffer?.list) {
        window.electronBuffer.list().then(setRichBuffers).catch(() => {});
      }
      // Focus search after animation
      setTimeout(() => searchRef.current?.focus(), 250);
    } else {
      // Reset state when closing
      setDetailBuffer(null);
      setSearchQuery('');
      setConfirmDelete(null);
      setRenamingId(null);
      setIsCreating(false);
    }
  }, [isOpen, refreshBuffers, isElectron]);

  // Focus create input
  useEffect(() => {
    if (isCreating) createRef.current?.focus();
  }, [isCreating]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (detailBuffer) {
          setDetailBuffer(null);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, detailBuffer, onClose]);

  // ─────────────────────────────────────────────────────────────────────
  // FILTER + SEARCH
  // ─────────────────────────────────────────────────────────────────────

  // Merge BufferSummary with rich data for metadata
  const enrichedBuffers = useMemo(() => {
    return buffers.map(buf => {
      const rich = richBuffers.find(r => r.id === (buf.id ?? buf.name));
      return { ...buf, rich };
    });
  }, [buffers, richBuffers]);

  const filteredBuffers = useMemo(() => {
    let list = enrichedBuffers;

    // Type filter
    if (typeFilter !== 'all') {
      list = list.filter(b => {
        const bt = b.rich?.origin?.metadata?.bufferType as BufferType | undefined
          ?? (b as unknown as { bufferType?: BufferType }).bufferType;
        return bt === typeFilter;
      });
    }

    // Search filter (searches name + content preview + source)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(b => {
        const preview = contentPreview(b.rich?.text);
        const source = sourceLabel(b.rich);
        return b.name.toLowerCase().includes(q)
          || preview.toLowerCase().includes(q)
          || source.toLowerCase().includes(q);
      });
    }

    // Sort
    list = [...list].sort((a, b) => {
      if (sortMode === 'name-asc' || sortMode === 'name-desc') {
        const cmp = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
        return sortMode === 'name-asc' ? cmp : -cmp;
      }
      const aTime = a.rich?.updatedAt ?? new Date(a.updatedAt).getTime();
      const bTime = b.rich?.updatedAt ?? new Date(b.updatedAt).getTime();
      return sortMode === 'date-desc' ? bTime - aTime : aTime - bTime;
    });

    return list;
  }, [enrichedBuffers, typeFilter, searchQuery, sortMode]);

  // ─────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────

  const handleSwitch = useCallback(async (nameOrId: string) => {
    try {
      await switchBuffer(nameOrId);
      onClose();
    } catch (err) {
      log.error('Failed to switch buffer:', err);
    }
  }, [switchBuffer, onClose]);

  const handleCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    await createBuffer(name);
    await switchBuffer(name);
    setNewName('');
    setNewType('working');
    setIsCreating(false);
    refreshBuffers();
  }, [newName, createBuffer, switchBuffer, refreshBuffers]);

  const handleDelete = useCallback(async (nameOrId: string) => {
    if (confirmDelete !== nameOrId) {
      setConfirmDelete(nameOrId);
      return;
    }
    try {
      const buf = enrichedBuffers.find(b => b.name === nameOrId || b.id === nameOrId);
      const bufType = buf?.rich?.origin?.metadata?.bufferType as string | undefined
        ?? (buf as unknown as { bufferType?: string })?.bufferType;
      const force = bufType === 'voice' || bufType === 'arc';
      await deleteBuffer(nameOrId, force);
      setConfirmDelete(null);
      // Update rich buffers locally to stay in sync
      setRichBuffers(prev => prev.filter(r => r.id !== nameOrId));
      refreshBuffers();
    } catch (err) {
      log.error('Delete buffer failed:', err);
      setConfirmDelete(null);
    }
  }, [confirmDelete, enrichedBuffers, deleteBuffer, refreshBuffers]);

  const handleRename = useCallback(async () => {
    if (!renamingId || !renameValue.trim() || !window.electronBuffer?.rename) return;
    try {
      await window.electronBuffer.rename(renamingId, renameValue.trim());
      refreshBuffers();
      if (isElectron && window.electronBuffer?.list) {
        window.electronBuffer.list().then(setRichBuffers).catch(() => {});
      }
    } catch (err) {
      log.error('Rename failed:', err);
    }
    setRenamingId(null);
    setRenameValue('');
  }, [renamingId, renameValue, refreshBuffers, isElectron]);

  const handleAutoName = useCallback(async () => {
    if (!isElectron || !window.electronBuffer?.rename) return;
    let renamed = 0;
    for (const buf of richBuffers) {
      // Build name from electronBufferToSummary-style logic
      const currentName = buffers.find(b => b.id === buf.id)?.name ?? '';
      if (!isGenericName(currentName)) continue;
      // Derive name from first 80 chars of content
      const clean = buf.text?.replace(/<!--[^>]*-->\n?/g, '').replace(/^#+\s*/, '').trim() ?? '';
      const firstLine = clean.split('\n')[0]?.trim() ?? '';
      if (!firstLine) continue;
      const newName = firstLine.length > 80 ? firstLine.slice(0, 77).trimEnd() + '...' : firstLine;
      if (newName === currentName) continue;
      try {
        await window.electronBuffer.rename(buf.id, newName);
        renamed++;
      } catch { /* skip failures */ }
    }
    if (renamed > 0) {
      refreshBuffers();
      window.electronBuffer.list?.().then(setRichBuffers).catch(() => {});
    }
    log.info(`Auto-named ${renamed} buffers`);
  }, [isElectron, richBuffers, buffers, refreshBuffers]);

  // ─────────────────────────────────────────────────────────────────────
  // DETAIL PANEL (Phase 2)
  // ─────────────────────────────────────────────────────────────────────

  const openDetail = useCallback(async (bufferId: string) => {
    if (!isElectron || !window.electronBuffer) return;
    setLoadingDetail(true);
    setDetailTab('versions');
    try {
      const data = await window.electronBuffer.load(bufferId);
      if (data) {
        setDetailBuffer(data);
        // Load versions
        const vers = await window.electronBuffer.listVersions(bufferId);
        setVersions(vers.sort((a, b) => b.createdAt - a.createdAt));
        // Load similar (fire-and-forget)
        window.electronBuffer.findSimilar(bufferId, 5).then(sims => {
          setSimilarBuffers(sims.map(s => ({
            id: s.id,
            name: (s as unknown as { name?: string }).name ?? s.id.slice(0, 8),
            wordCount: s.wordCount,
            similarity: s.similarity,
          })));
        }).catch(() => setSimilarBuffers([]));
      }
    } catch (err) {
      log.error('Failed to load buffer detail:', err);
    }
    setLoadingDetail(false);
  }, [isElectron]);

  const handleRestore = useCallback(async (versionId: string) => {
    if (!detailBuffer || !window.electronBuffer?.restoreVersion) return;
    try {
      await window.electronBuffer.restoreVersion(detailBuffer.id, versionId);
      // Reload versions
      const vers = await window.electronBuffer.listVersions(detailBuffer.id);
      setVersions(vers.sort((a, b) => b.createdAt - a.createdAt));
      refreshBuffers();
    } catch (err) {
      log.error('Restore failed:', err);
    }
  }, [detailBuffer, refreshBuffers]);

  const handleCreateCheckpoint = useCallback(async () => {
    if (!detailBuffer || !window.electronBuffer?.createVersion) return;
    try {
      await window.electronBuffer.createVersion(
        detailBuffer.id,
        checkpointLabel.trim() || 'manual checkpoint'
      );
      setCheckpointLabel('');
      const vers = await window.electronBuffer.listVersions(detailBuffer.id);
      setVersions(vers.sort((a, b) => b.createdAt - a.createdAt));
    } catch (err) {
      log.error('Checkpoint failed:', err);
    }
  }, [detailBuffer, checkpointLabel]);

  // ─────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────

  const typeFilters: { key: TypeFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'working', label: 'Working' },
    { key: 'voice', label: 'Voice' },
    { key: 'reference', label: 'Ref' },
    { key: 'arc', label: 'Arc' },
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="buffer-manager__overlay"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`buffer-manager__drawer${isOpen ? ' buffer-manager__drawer--open' : ''}`}
        role="dialog"
        aria-label="Manage Buffers"
        aria-modal="true"
      >
        {/* Header */}
        <div className="buffer-manager__header">
          {detailBuffer ? (
            <>
              <button
                className="buffer-manager__back-btn"
                onClick={() => setDetailBuffer(null)}
                aria-label="Back to list"
              >
                &larr;
              </button>
              <h2 className="buffer-manager__title buffer-manager__title--detail">
                {richBuffers.find(r => r.id === detailBuffer.id)
                  ? (richBuffers.find(r => r.id === detailBuffer.id) as BufferContentData & { name?: string })
                    ?.origin?.metadata?.title as string ?? detailBuffer.id.slice(0, 20)
                  : detailBuffer.id.slice(0, 20)}
              </h2>
              <button
                className="buffer-manager__open-btn"
                onClick={() => {
                  const buf = buffers.find(b => (b.id ?? b.name) === detailBuffer.id);
                  if (buf) handleSwitch(buf.id ?? buf.name);
                }}
              >
                Open
              </button>
            </>
          ) : (
            <>
              <h2 className="buffer-manager__title">Manage Buffers</h2>
              <button
                className="buffer-manager__close-btn"
                onClick={onClose}
                aria-label="Close"
              >
                &times;
              </button>
            </>
          )}
        </div>

        {detailBuffer ? (
          /* ═══════════ DETAIL VIEW (Phase 2) ═══════════ */
          <div className="buffer-manager__detail">
            {/* Metadata */}
            <div className="buffer-manager__meta-grid">
              <div className="buffer-manager__meta-item">
                <span className="buffer-manager__meta-label">State</span>
                <span className="buffer-manager__meta-value">{detailBuffer.state}</span>
              </div>
              <div className="buffer-manager__meta-item">
                <span className="buffer-manager__meta-label">Words</span>
                <span className="buffer-manager__meta-value">{detailBuffer.wordCount.toLocaleString()}</span>
              </div>
              <div className="buffer-manager__meta-item">
                <span className="buffer-manager__meta-label">Format</span>
                <span className="buffer-manager__meta-value">{detailBuffer.format}</span>
              </div>
              <div className="buffer-manager__meta-item">
                <span className="buffer-manager__meta-label">Updated</span>
                <span className="buffer-manager__meta-value">{relativeTime(detailBuffer.updatedAt)}</span>
              </div>
              <div className="buffer-manager__meta-item">
                <span className="buffer-manager__meta-label">Origin</span>
                <span className="buffer-manager__meta-value">{detailBuffer.origin.sourceType}</span>
              </div>
              <div className="buffer-manager__meta-item">
                <span className="buffer-manager__meta-label">Transforms</span>
                <span className="buffer-manager__meta-value">{detailBuffer.provenanceChain?.transformationCount ?? 0}</span>
              </div>
            </div>

            {/* Tabs */}
            <div className="buffer-manager__tabs">
              {(['versions', 'timeline', 'similar'] as DetailTab[]).map(tab => (
                <button
                  key={tab}
                  className={`buffer-manager__tab${detailTab === tab ? ' buffer-manager__tab--active' : ''}`}
                  onClick={() => setDetailTab(tab)}
                >
                  {tab === 'versions' ? 'Versions' : tab === 'timeline' ? 'Timeline' : 'Similar'}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="buffer-manager__tab-content">
              {loadingDetail && <div className="buffer-manager__loading">Loading...</div>}

              {detailTab === 'versions' && !loadingDetail && (
                <div className="buffer-manager__versions">
                  {/* Create checkpoint */}
                  <div className="buffer-manager__checkpoint">
                    <input
                      className="buffer-manager__checkpoint-input"
                      placeholder="Checkpoint label..."
                      value={checkpointLabel}
                      onChange={e => setCheckpointLabel(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleCreateCheckpoint(); }}
                    />
                    <button
                      className="buffer-manager__checkpoint-btn"
                      onClick={handleCreateCheckpoint}
                    >
                      + Checkpoint
                    </button>
                  </div>

                  {versions.length === 0 && (
                    <div className="buffer-manager__empty">No versions yet</div>
                  )}

                  {versions.map(v => (
                    <div key={v.id} className="buffer-manager__version-card">
                      <div className="buffer-manager__version-info">
                        <span className="buffer-manager__version-label">{v.label}</span>
                        <span className="buffer-manager__version-meta">
                          {formatWords(v.wordCount)}w &middot; {relativeTime(v.createdAt)}
                        </span>
                      </div>
                      <button
                        className="buffer-manager__version-restore"
                        onClick={() => handleRestore(v.id)}
                      >
                        Restore
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {detailTab === 'timeline' && !loadingDetail && (
                <div className="buffer-manager__timeline-wrap">
                  <BufferTimeline
                    bufferId={detailBuffer.id}
                    onRestore={(eventId) => handleRestore(eventId)}
                  />
                </div>
              )}

              {detailTab === 'similar' && !loadingDetail && (
                <div className="buffer-manager__similar">
                  {similarBuffers.length === 0 && (
                    <div className="buffer-manager__empty">No similar buffers found</div>
                  )}
                  {similarBuffers.map(s => (
                    <button
                      key={s.id}
                      className="buffer-manager__similar-card"
                      onClick={() => handleSwitch(s.id)}
                    >
                      <span className="buffer-manager__similar-name">{s.name}</span>
                      <span className="buffer-manager__similar-meta">
                        {formatWords(s.wordCount)}w &middot; {Math.round(s.similarity * 100)}%
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ═══════════ LIST VIEW (Phase 1) ═══════════ */
          <div className="buffer-manager__list-view">
            {/* Search */}
            <div className="buffer-manager__search">
              <input
                ref={searchRef}
                className="buffer-manager__search-input"
                type="text"
                placeholder="Search buffers..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Type filter pills */}
            <div className="buffer-manager__filters">
              {typeFilters.map(f => (
                <button
                  key={f.key}
                  className={`buffer-manager__filter-pill${typeFilter === f.key ? ' buffer-manager__filter-pill--active' : ''}`}
                  onClick={() => setTypeFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Action bar: + New, sort toggles, auto-name */}
            <div className="buffer-manager__action-bar">
              {isCreating ? (
                <form className="buffer-manager__create-form" onSubmit={handleCreate}>
                  <input
                    ref={createRef}
                    className="buffer-manager__create-input"
                    type="text"
                    placeholder="Buffer name..."
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Escape') { setIsCreating(false); setNewName(''); } }}
                  />
                  <select
                    className="buffer-manager__create-type"
                    value={newType}
                    onChange={e => setNewType(e.target.value as BufferType)}
                  >
                    <option value="working">W</option>
                    <option value="voice">V</option>
                    <option value="reference">R</option>
                    <option value="arc">A</option>
                  </select>
                  <button className="buffer-manager__create-submit" type="submit">+</button>
                  <button
                    className="buffer-manager__create-cancel"
                    type="button"
                    onClick={() => { setIsCreating(false); setNewName(''); }}
                  >
                    &times;
                  </button>
                </form>
              ) : (
                <>
                  <button
                    className="buffer-manager__compact-btn"
                    onClick={() => setIsCreating(true)}
                    title="Create new buffer"
                  >
                    +
                  </button>
                  <span className="buffer-manager__action-bar-sep" />
                  <button
                    className={`buffer-manager__sort-btn${sortMode.startsWith('date') ? ' buffer-manager__sort-btn--active' : ''}`}
                    onClick={() => setSortMode(m => m === 'date-desc' ? 'date-asc' : 'date-desc')}
                    title={sortMode === 'date-desc' ? 'Date: newest first' : sortMode === 'date-asc' ? 'Date: oldest first' : 'Sort by date'}
                  >
                    {sortMode === 'date-asc' ? '\u2191' : '\u2193'} Date
                  </button>
                  <button
                    className={`buffer-manager__sort-btn${sortMode.startsWith('name') ? ' buffer-manager__sort-btn--active' : ''}`}
                    onClick={() => setSortMode(m => m === 'name-asc' ? 'name-desc' : 'name-asc')}
                    title={sortMode === 'name-asc' ? 'Name: A\u2013Z' : sortMode === 'name-desc' ? 'Name: Z\u2013A' : 'Sort by name'}
                  >
                    {sortMode === 'name-desc' ? '\u2193' : '\u2191'} A-Z
                  </button>
                  {isElectron && (
                    <>
                      <span className="buffer-manager__action-bar-sep" />
                      <button
                        className="buffer-manager__sort-btn"
                        onClick={handleAutoName}
                        title="Auto-name generic buffers from content"
                      >
                        Aa
                      </button>
                    </>
                  )}
                </>
              )}
            </div>

            {/* Buffer list */}
            <div className="buffer-manager__list">
              {filteredBuffers.length === 0 && (
                <div className="buffer-manager__empty">
                  {searchQuery ? 'No buffers match your search' : 'No buffers'}
                </div>
              )}

              {filteredBuffers.map(buf => {
                const isActive = buf.name === activeBufferName;
                const switchKey = buf.id ?? buf.name;
                const deleteKey = buf.id ?? buf.name;
                const bufType = buf.rich?.origin?.metadata?.bufferType as BufferType | undefined
                  ?? (buf as unknown as { bufferType?: BufferType }).bufferType;
                const state = buf.rich?.state;
                const wordCount = buf.rich?.wordCount ?? buf.contentLength;
                const updatedAt = buf.rich?.updatedAt ?? buf.updatedAt;
                const source = sourceLabel(buf.rich);
                const displayName = cleanBufferName(buf.name, buf.id?.slice(0, 8) ?? 'Untitled');
                const preview = isGenericName(displayName) || isGenericName(buf.name) ? contentPreview(buf.rich?.text) : '';
                const fullTooltip = [
                  displayName,
                  preview && preview !== displayName ? preview : '',
                  source,
                  `${formatWords(wordCount)} words`,
                ].filter(Boolean).join('\n');

                return (
                  <div
                    key={switchKey}
                    className={`buffer-manager__card${isActive ? ' buffer-manager__card--active' : ''}`}
                    title={fullTooltip}
                  >
                    {/* Type badge — left column */}
                    {bufType && (
                      <span className={`buffer-manager__type buffer-manager__type--${bufType}`}>
                        {TYPE_LABELS[bufType] ?? 'W'}
                      </span>
                    )}

                    {/* Two-line content — center column */}
                    <div className="buffer-manager__card-body">
                      {/* Line 1: Name */}
                      {renamingId === switchKey ? (
                        <input
                          className="buffer-manager__rename-input"
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleRename();
                            if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                          }}
                          onBlur={() => handleRename()}
                          autoFocus
                        />
                      ) : (
                        <button
                          className="buffer-manager__card-name"
                          onClick={() => handleSwitch(switchKey)}
                        >
                          {displayName}
                        </button>
                      )}

                      {/* Line 2: Preview / source / meta */}
                      <div className="buffer-manager__card-line2">
                        {preview && (
                          <span className="buffer-manager__card-preview">{preview}</span>
                        )}
                        {source && (
                          <span className="buffer-manager__card-source">{source}</span>
                        )}
                        <span className="buffer-manager__card-meta">
                          {formatWords(wordCount)}w
                          {updatedAt && <> &middot; {relativeTime(updatedAt)}</>}
                          {state && (
                            <span
                              className={`buffer-manager__state-dot buffer-manager__state-dot--${state}`}
                              title={state}
                            />
                          )}
                        </span>
                      </div>
                    </div>

                    {/* Actions — right column */}
                    <div className="buffer-manager__card-actions">
                      {/* Detail button (Electron only) */}
                      {isElectron && buf.id && (
                        <button
                          className="buffer-manager__action-btn"
                          onClick={() => openDetail(buf.id!)}
                          title="Details"
                          aria-label="Buffer details"
                        >
                          &#x2139;
                        </button>
                      )}

                      {/* Rename button (Electron only) */}
                      {isElectron && window.electronBuffer?.rename && renamingId !== switchKey && (
                        <button
                          className="buffer-manager__action-btn"
                          onClick={() => { setRenamingId(switchKey); setRenameValue(buf.name); }}
                          title="Rename"
                          aria-label="Rename buffer"
                        >
                          &#x270E;
                        </button>
                      )}

                      {/* Delete button */}
                      {buf.name !== 'workspace' && (
                        <button
                          className={`buffer-manager__action-btn buffer-manager__action-btn--delete${confirmDelete === deleteKey ? ' buffer-manager__action-btn--confirm' : ''}`}
                          onClick={() => handleDelete(deleteKey)}
                          title={confirmDelete === deleteKey ? 'Click again to confirm' : 'Delete buffer'}
                          aria-label={confirmDelete === deleteKey ? 'Confirm delete' : 'Delete buffer'}
                        >
                          {confirmDelete === deleteKey ? '!!' : '\u00D7'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
